import { teamGroupBadgeStyle } from '@/utils/teamGroupColor'

/** The minimum a badge needs. Both `TeamGroupDto` and the lighter row refs satisfy it. */
export interface TeamGroupBadgeRef {
    id: string
    name: string
    color: string | null
}

interface TeamGroupBadgeProps {
    group: TeamGroupBadgeRef
    /** `sm` for dense lists (member rows, table cells), `md` for a standalone label. */
    size?: 'sm' | 'md'
    /** Constrain to the container and ellipsise — for the group list, where a long name would push the row. */
    truncate?: boolean
}

/**
 * One group, in its own colour (ADR-027 §5.1).
 *
 * Exists because the same pill was hand-written in four places — the member rows, the group list, the Team
 * table's Group column and the create/edit picker — and the fourth had already drifted to a different
 * padding. A group has to look identical everywhere or the colour stops being the thing that identifies it.
 */
export const TeamGroupBadge = ({ group, size = 'sm', truncate }: TeamGroupBadgeProps) => {
    const badge = teamGroupBadgeStyle(group.id, group.color)
    return (
        <span
            className={`rounded-full font-medium ${size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs'} ${
                truncate ? 'inline-block max-w-full truncate' : ''
            } ${badge.className}`}
            style={badge.style}
        >
            {group.name}
        </span>
    )
}

interface TeamGroupBadgesProps {
    groups: TeamGroupBadgeRef[]
    /** Shown instead of the badges when the member is in none. Omit to render nothing at all. */
    emptyLabel?: string
    size?: 'sm' | 'md'
}

/** A member's groups, or the empty-state label. */
export const TeamGroupBadges = ({ groups, emptyLabel, size }: TeamGroupBadgesProps) => {
    if (groups.length === 0) {
        return emptyLabel ? <span className="text-xs text-slate-400 dark:text-slate-500">{emptyLabel}</span> : null
    }
    return (
        <div className="flex flex-wrap items-center gap-1">
            {groups.map((group) => (
                <TeamGroupBadge key={group.id} group={group} size={size} />
            ))}
        </div>
    )
}
